/**
 * Google Tag Manager — standalone top-level page.
 *
 * Split out of the Meta CAPI page (Phase 4 of the tracking nav
 * cleanup). Rationale:
 *   1. Mental model: Meta CAPI = Facebook server events, GTM = Google
 *      universal tag container. Operators looking for "GTM" never
 *      thought to click "Meta CAPI", killing discoverability.
 *   2. Plan tier mismatch: gtmAutoSync is Enterprise-only while
 *      metaCapi is Agency+. Hiding GTM behind the Agency-gated CAPI
 *      page meant Agency users saw a Connect button that 403'd
 *      server-side — UX lied. Lower tiers couldn't even see it as an
 *      upgrade hook.
 *   3. Future surface: when we add Google Ads server conversions,
 *      GA4 Measurement Protocol, etc., they all naturally belong on
 *      this Google-side page rather than further bloating the CAPI page.
 *
 * The actual config UI lives in `GtmConnectPanel` — we wrap it in a
 * page-level header + info strip + footer guide so the operator sees
 * what they're getting before clicking Connect.
 */

import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserLayout } from '../components/User/UserLayout';
import { GtmConnectPanel } from '../components/Common/GtmConnectPanel';
import { GoogleTagManagerBrandIcon } from '../components/Common/BrandIcons';
import { Boxes, Layers, ShieldCheck, ExternalLink, Lock } from 'lucide-react';
import { useEffectivePlan } from '../store/accountStore';
import { hasFeature } from '../constants/planLimits';

export const TagManager: React.FC = () => {
  const { t } = useTranslation('tracking');
  const navigate = useNavigate();
  const effectivePlan = useEffectivePlan();
  const hasAccess = hasFeature(effectivePlan, 'gtmAutoSync');

  // Free / Pro / Agency see a locked card instead of the live config UI.
  // Render INSIDE UserLayout so the sidebar stays mounted — dropping the
  // user on a different layout would feel like a 404. Server-side gates
  // (gtm.js + planLimits.js) are the real defense; this card is the
  // conversion surface that turns the lock into an upgrade.
  if (!hasAccess) {
    return (
      <UserLayout>
        <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500/15 via-indigo-500/15 to-purple-500/15 ring-1 ring-blue-500/20">
                <Lock className="h-7 w-7 text-blue-600" strokeWidth={2.25} />
              </div>
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-gray-900">{t('tagManager.title')}</h1>
                  <span className="rounded-full bg-gradient-to-r from-amber-500 to-orange-600 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
                    Enterprise
                  </span>
                </div>
                <p className="mb-4 text-sm text-gray-600">{t('tagManager.lockedDesc')}</p>
                <ul className="mb-5 space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span>
                    <span>{t('tagManager.lockedBenefits.oneClick')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span>
                    <span>{t('tagManager.lockedBenefits.officialApi')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span>
                    <span>{t('tagManager.lockedBenefits.perPage')}</span>
                  </li>
                </ul>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-500/20 hover:shadow-xl hover:shadow-amber-500/30 transition-all"
                  >
                    {t('tagManager.lockedCta')}
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center justify-center rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    {t('tagManager.lockedBack')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        {/* Page header — uses Google brand-ish blue/indigo gradient to
            visually distinguish from the Meta CAPI page (emerald/teal). */}
        <header className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-shrink-0" aria-hidden="true">
              <GoogleTagManagerBrandIcon
                box={48}
                glyph={32}
                rounded="rounded-xl"
                className="shadow-sm ring-1 ring-gray-200/80"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('tagManager.title')}</h1>
              <p className="text-sm text-gray-600">{t('tagManager.subtitle')}</p>
            </div>
          </div>
        </header>

        {/* "How this works" strip — same 3-card layout as MetaCapi for
            visual consistency across the tracking section. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <InfoCard
            icon={<Boxes className="w-5 h-5 text-blue-600" />}
            title={t('tagManager.info.oneClick.title')}
            body={t('tagManager.info.oneClick.body')}
          />
          <InfoCard
            icon={<Layers className="w-5 h-5 text-indigo-600" />}
            title={t('tagManager.info.perPage.title')}
            body={t('tagManager.info.perPage.body')}
          />
          <InfoCard
            icon={<ShieldCheck className="w-5 h-5 text-emerald-600" />}
            title={t('tagManager.info.noManual.title')}
            body={t('tagManager.info.noManual.body')}
          />
        </div>

        {/* Main settings panel */}
        <div className="mb-6">
          <GtmConnectPanel />
        </div>

        {/* Footer guidance — mirrors the meta.guide footer pattern so
            users find the next step without needing extra clicks. */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            {t('tagManager.guide.title')}
          </h3>
          <ol className="text-sm text-blue-900 space-y-1.5 list-decimal list-inside">
            <li>{t('tagManager.guide.step1')}</li>
            <li>{t('tagManager.guide.step2')}</li>
            <li>{t('tagManager.guide.step3')}</li>
            <li>{t('tagManager.guide.step4')}</li>
            <li>{t('tagManager.guide.step5')}</li>
          </ol>
          <p className="text-xs text-blue-700 mt-3">
            <Trans i18nKey="tagManager.guide.footer" t={t}>
              HitzLink only requests permission to edit container tags, triggers, variables, and
              workspaces.
            </Trans>{' '}
            <a
              href="https://tagmanager.google.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-700 underline hover:text-blue-900 inline-flex items-center gap-1"
            >
              tagmanager.google.com <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
      </div>
    </UserLayout>
  );
};

const InfoCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  body: string;
}> = ({ icon, title, body }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-1.5">
      {icon}
      <span className="text-sm font-semibold text-gray-900">{title}</span>
    </div>
    <p className="text-xs text-gray-600 leading-relaxed">{body}</p>
  </div>
);
